import React, { Component } from "react";
import TabSection from "./TabSection";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as compose from "lodash.flowright";
import * as Cookies from "es-cookie";
import moment from "moment";
import CardLessDataTableWithSearch from "../datatables/CardLessDataTableWithSearch";
import { FeePageShimmer } from "../FeeStructure/FeeShimmer";

const GET_STUDENT_FEE_REPORT = gql`
    query getStudentFeeReport($student_id: Int, $academic_year_id: Int) {
        getStudentFeeReport(student_id: $student_id, academic_year_id: $academic_year_id) {
            fee_type_id
            fee_type_name
            total_fee
            discount
            fee_amount
            terms {
                term_no
                term_amount
                term_date
                is_paid
                paid_amount
                paid_date
                payment_mode_id
                payment_id
                comments
                is_due_entry
                due_from_term_no
            }
        }
    }
`;

const GET_ORGANISATION_PROFILE = gql`
    query($institution_id: Int!) {
        getOrganisationProfile(institution_id: $institution_id) {
            id
            organisation_name
            logo
            complete_address
        }
    }
`;

const FETCH_PAYMENT_MODES = gql`
    query($id: Int) {
        getPaymentModes(id: $id) {
            id
            payment_mode
        }
    }
`;

const rupee = (v) => `₹${(parseInt(v) || 0).toLocaleString("en-IN")}`;
const fmtDate = (unix) => (unix ? moment.unix(parseInt(unix)).format("DD MMM YYYY") : "—");

// edu_student_id is preferred; fall back to student_user_id, then student_id.
const resolveStudentId = (student) => {
  if (!student) return null;
  if (student.edu_student_id !== null && student.edu_student_id !== undefined) {
    return parseInt(student.edu_student_id);
  }
  if (student.student_user_id !== null && student.student_user_id !== undefined) {
    return parseInt(student.student_user_id);
  }
  if (student.student_id !== null && student.student_id !== undefined) {
    return parseInt(student.student_id);
  }
  return null;
};

const buildVariables = (student) => ({
  student_id: resolveStudentId(student),
  academic_year_id: parseInt(student?.academic_year_id) || 2,
});

// RECEIPT cell — a clickable download affordance for paid terms, a pending badge otherwise.
const makeReceiptFormatter = (onDownload) => (cell, row) => {
  if (row.is_paid) {
    return (
      <span
        onClick={() => onDownload(row)}
        className="d-inline-flex align-items-center"
        style={{
          gap: "8px",
          padding: "6px 14px",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          background: "#ffffff",
          color: "#475569",
          fontWeight: 600,
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        <i className="fas fa-file-invoice" style={{ fontSize: "12px" }}></i>
        Download
      </span>
    );
  }
  return (
    <span
      style={{
        background: "#fef3c7",
        color: "#b45309",
        padding: "4px 14px",
        borderRadius: "20px",
        fontWeight: 600,
        fontSize: "12px",
      }}
    >
      Pending
    </span>
  );
};

class FeesTab extends Component {
  // Organisation profile / payment-mode list resolved from the query HOCs.
  get orgProfile() {
    return this.props.getOrganisationProfile?.getOrganisationProfile || null;
  }

  get paymentModes() {
    return this.props.getPaymentModes?.getPaymentModes || [];
  }

  // Header (org name / address / logo / student) shared by both receipt views.
  receiptHeader = () => {
    const org = this.orgProfile;
    const student = this.props.student || {};
    return {
      orgName: org?.organisation_name || "Rizee College",
      address: org?.complete_address || student?.branch_name || "College Branch Office",
      logoDataUrl: org?.logo ? `https://support.rizee.in/files/${org.logo}` : "",
      studentName: student?.student_name || "—",
      classNameStr: student?.class_name || "—",
      admissionNoStr: student?.admission_no || student?.roll_no || "—",
      parentName: student?.parent_name || student?.father_name || "",
      academicYear: student?.academic_year_name || student?.year_name || "",
    };
  };

  // "Download All" — one consolidated payment-history sheet for every paid term.
  downloadAllPayments = (paymentList, totalPaid) => {
    if (!paymentList || paymentList.length === 0) {
      window.alert("No payments found to download.");
      return;
    }
    const { orgName, address, logoDataUrl, studentName, classNameStr, admissionNoStr, parentName, academicYear } =
      this.receiptHeader();
    const classAdm = [classNameStr, admissionNoStr].filter(Boolean).join(" / Adm No: ");

    const sorted = [...paymentList].sort((a, b) => (parseInt(a.paid_date) || 0) - (parseInt(b.paid_date) || 0));

    // Fee-level totals across all fee types in the report.
    const report = this.props.getStudentFeeReport?.getStudentFeeReport || [];
    const totalFee = report.reduce((s, ft) => s + (parseInt(ft.total_fee) || 0), 0);
    const feeDiscount = report.reduce((s, ft) => s + (parseInt(ft.discount) || 0), 0);
    const feeAmount = report.reduce(
      (s, ft) => s + (parseInt(ft.fee_amount) || (parseInt(ft.total_fee) || 0) - (parseInt(ft.discount) || 0)),
      0
    );
    const totalDiscount = sorted.reduce((s, p) => s + (parseInt(p.discount) || 0), 0);
    const overallDue = Math.max(0, feeAmount - totalDiscount - (totalPaid || 0));

    const rowsHtml = sorted
      .map((p, idx) => {
        const term = p.is_due ? `Due (Term ${p.due_from || p.term_no})` : `Term ${p.term_no}`;
        const date = p.paid_date ? moment.unix(parseInt(p.paid_date)).format("DD/MMM/YYYY") : "—";
        return `<tr>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:center">${idx + 1}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${p.receipt_no || "—"}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${p.fee_type || "—"}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${term}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${date}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb">${p.payment_mode || "—"}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:right">${(p.discount || 0) > 0 ? "&#x20B9;" + (p.discount || 0).toLocaleString("en-IN") : "—"}</td>
                <td style="padding:7px 10px;border:1px solid #e5e7eb;text-align:right;font-weight:700">&#x20B9;${(p.amount || 0).toLocaleString("en-IN")}</td>
            </tr>`;
      })
      .join("");

    const infoRows = [
      academicYear ? ["Academic Year", academicYear] : null,
      ["Student Name", studentName || "—"],
      parentName ? ["Parent Name", parentName] : null,
      ["Class / Adm No", classAdm || "—"],
    ]
      .filter(Boolean)
      .map(
        ([label, value]) => `
            <tr>
                <td style="padding:4px 0;font-size:12px;color:#555;width:160px">${label}</td>
                <td style="padding:4px 0;font-size:12px;color:#555;width:14px;text-align:center">:</td>
                <td style="padding:4px 0;font-size:12px;font-weight:700;color:#111">${value}</td>
            </tr>`
      )
      .join("");

    const win = window.open("", "_blank", "width=900,height=750");
    win.document.write(`<html><head><title>Payment History</title><style>
            *{margin:0;padding:0;box-sizing:border-box}
            body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:12px}
            .receipt-wrapper{width:800px;margin:0 auto;border:1.5px solid #333}
            .receipt-header{display:flex;align-items:flex-start;justify-content:space-between;padding:14px 18px 10px;border-bottom:1.5px solid #333}
            .hdr-left{display:flex;align-items:flex-start;gap:14px;flex:1}
            .hdr-left img{width:auto;height:60px;object-fit:contain;flex-shrink:0}
            .org-name{font-size:18px;font-weight:700;color:#111;margin-bottom:2px}
            .org-addr{font-size:11px;color:#555;line-height:1.5}
            .receipt-title{text-align:center;font-size:16px;font-weight:700;padding:10px 0;border-bottom:1.5px solid #333}
            .receipt-body{padding:14px 18px}
            .history-table{width:100%;border-collapse:collapse;margin-top:12px;font-size:12px}
            .history-table th{padding:8px 10px;border:1px solid #333;background:#f3f4f6;font-size:12px;text-align:left}
            .totals{margin-top:14px;display:flex;flex-wrap:wrap;justify-content:flex-end;gap:12px 30px;font-size:13px}
            .receipt-footer{border-top:1.5px solid #333;padding:10px 18px}
            @media print{html,body{height:auto!important}body{padding:2px}@page{size:auto;margin:5mm}}
        </style></head><body>
            <div class="receipt-wrapper">
                <div class="receipt-header">
                    <div class="hdr-left">
                        ${logoDataUrl ? `<img src="${logoDataUrl}" />` : ""}
                        <div>
                            <div class="org-name">${orgName}</div>
                            <div class="org-addr">${address}</div>
                        </div>
                    </div>
                    <div style="text-align:right;font-size:10px;color:#333;min-width:160px;line-height:1.9">
                        <div>Date: <strong>${moment().format("DD/MMM/YYYY")}</strong></div>
                        <div>Total Receipts: <strong>${sorted.length}</strong></div>
                    </div>
                </div>
                <div class="receipt-title">Payment History</div>
                <div class="receipt-body">
                    <table style="width:100%;border-collapse:collapse"><tbody>${infoRows}</tbody></table>
                    <table class="history-table">
                        <thead>
                            <tr>
                                <th style="text-align:center;width:40px">#</th>
                                <th>Receipt No</th>
                                <th>Fee Type</th>
                                <th>Term</th>
                                <th>Paid Date</th>
                                <th>Mode</th>
                                <th style="text-align:right">Discount</th>
                                <th style="text-align:right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>${rowsHtml}</tbody>
                    </table>
                    <div class="totals">
                        ${totalFee > 0 ? `<div>Total Fee: <strong style="color:#111">&#x20B9;${totalFee.toLocaleString("en-IN")}</strong></div>` : ""}
                        ${feeDiscount > 0 ? `<div>Fee Discount: <strong style="color:#1d4ed8">&#x20B9;${feeDiscount.toLocaleString("en-IN")}</strong></div>` : ""}
                        ${totalDiscount > 0 ? `<div>Term Discount: <strong style="color:#1d4ed8">&#x20B9;${totalDiscount.toLocaleString("en-IN")}</strong></div>` : ""}
                        <div>Total Paid: <strong style="color:#15803d">&#x20B9;${(totalPaid || 0).toLocaleString("en-IN")}</strong></div>
                        ${overallDue > 0 ? `<div>Balance Due: <strong style="color:#dc2626">&#x20B9;${overallDue.toLocaleString("en-IN")}</strong></div>` : ""}
                    </div>
                </div>
                <div class="receipt-footer">
                    <div style="text-align:center;font-size:10px;color:#555;margin-bottom:2px">Note: Once a payment is made, it cannot be refunded.</div>
                    <div style="text-align:center;font-size:10px;color:#555">Thank You</div>
                    <div style="text-align:right;font-size:11px;font-weight:700;color:#333;margin-top:28px">School Stamp &amp; Sign</div>
                </div>
            </div>
            <script>window.print();window.close();<\/script>
        </body></html>`);
    win.document.close();
    win.focus();
  };

  // Single-payment receipt for one paid term.
  viewPDF = (payment) => {
    const { orgName, address, logoDataUrl, studentName, classNameStr, admissionNoStr, parentName, academicYear } =
      this.receiptHeader();
    const classAdm = [classNameStr, admissionNoStr].filter(Boolean).join(" / Adm No: ");
    const receiptDate = payment.paid_date ? moment.unix(parseInt(payment.paid_date)).format("DD/MMM/YYYY") : "—";
    const amountPaid = ((payment.paid_amount != null ? payment.paid_amount : payment.amount) || 0).toLocaleString("en-IN");

    const dataRows = [
      academicYear ? ["Academic Year", academicYear] : null,
      ["Student Name", studentName || "—"],
      parentName ? ["Parent Name", parentName] : null,
      ["Class / Adm No", classAdm || "—"],
      ["Fee Type", `${payment.fee_type || "—"} – Term ${payment.term_no}`],
      ["Total Paid", `&#x20B9;${amountPaid} (${payment.payment_mode || "—"})`],
    ].filter(Boolean);

    const rowsHtml = dataRows
      .map(
        ([label, value]) => `
                <tr>
                    <td style="padding:6px 0;font-size:13px;color:#555;width:220px">${label}</td>
                    <td style="padding:6px 0;font-size:13px;color:#555;width:20px;text-align:center">:</td>
                    <td style="padding:6px 0;font-size:13px;font-weight:700;color:#111">${value}</td>
                </tr>`
      )
      .join("");

    const win = window.open("", "_blank", "width=800,height=700");
    win.document.write(`<html><head><title>Fee Receipt</title><style>
                *{margin:0;padding:0;box-sizing:border-box}
                body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:12px}
                .receipt-wrapper{width:700px;margin:0 auto;border:1.5px solid #333}
                .receipt-header{display:flex;align-items:center;gap:14px;padding:14px 18px 10px;border-bottom:1.5px solid #333}
                .hdr-logo{height:56px;width:auto;object-fit:contain;flex-shrink:0}
                .hdr-info{flex:1;text-align:center}
                .org-name{font-size:18px;font-weight:700;color:#111;margin-bottom:2px}
                .org-addr{font-size:11px;color:#555;line-height:1.5;margin:0 auto;max-width:500px}
                .receipt-title{display:flex;align-items:center;padding:10px 18px;border-bottom:1.5px solid #333}
                .rt-side{flex:1;font-size:11px;font-weight:400;color:#333;line-height:1.5}
                .rt-main{font-size:16px;font-weight:700;text-align:center}
                .receipt-body{padding:14px 18px}
                .receipt-footer{border-top:1.5px solid #333;padding:10px 18px}
                @media print{html,body{height:auto!important}body{padding:2px}@page{size:auto;margin:3mm}}
            </style></head><body>
                <div class="receipt-wrapper">
                    <div class="receipt-header">
                        ${logoDataUrl ? `<img class="hdr-logo" src="${logoDataUrl}" />` : ""}
                        <div class="hdr-info">
                            <div class="org-name">${orgName}</div>
                            <div class="org-addr">${address}</div>
                        </div>
                    </div>
                    <div class="receipt-title">
                        <div class="rt-side" style="text-align:left">Receipt No: <strong>${payment.receipt_no || "—"}</strong><br/>Adm No: <strong>${admissionNoStr || "—"}</strong></div>
                        <div class="rt-main">Fee Receipt</div>
                        <div class="rt-side" style="text-align:right">Date: <strong>${receiptDate}</strong></div>
                    </div>
                    <div class="receipt-body">
                        <table style="width:100%;border-collapse:collapse">
                            <tbody>${rowsHtml}</tbody>
                        </table>
                    </div>
                    <div class="receipt-footer">
                        <div style="text-align:center;font-size:10px;color:#555;line-height:1.3">Note: Once a payment is made, it cannot be refunded. Thank You</div>
                        <div style="text-align:right;font-size:11px;font-weight:700;color:#333;margin-top:16px">School Stamp &amp; Sign</div>
                    </div>
                </div>
                <script>window.print();window.close();<\/script>
            </body></html>`);
    win.document.close();
    win.focus();
  };

  // Flatten every term across all fee types into a totals summary, the
  // payment-history rows consumed by the table, and the paid-only list used
  // by the "Download All" sheet.
  buildModel = (feeReport) => {
    let totalFee = 0;
    let totalPaid = 0;
    const rows = [];
    const paymentList = [];
    const modes = this.paymentModes;

    feeReport.forEach((ft, ftIdx) => {
      const feeAmount =
        parseInt(ft.fee_amount) ||
        (parseInt(ft.total_fee) || 0) - (parseInt(ft.discount) || 0);
      totalFee += feeAmount;
      const discount = parseInt(ft.discount) || 0;

      (ft.terms || []).forEach((t, tIdx) => {
        const paidAmt = parseInt(t.paid_amount) || 0;
        totalPaid += paidAmt;
        const isPaid = t.is_paid == 1 || t.is_paid === true;
        const isDue = t.is_due_entry == 1;
        const pm = modes.find((p) => +p.id === +t.payment_mode_id);
        const paymentMode = pm?.payment_mode || "—";

        rows.push({
          id: `${ftIdx}-${tIdx}`,
          date: fmtDate(t.term_date),
          description: `${ft.fee_type_name} — Term ${t.term_no}`,
          amount: parseInt(t.term_amount) || 0,
          receipt: isPaid ? "Paid" : "Pending",
          is_paid: isPaid,
          // Fields used to render a receipt for this row.
          fee_type: ft.fee_type_name,
          term_no: t.term_no,
          paid_amount: paidAmt,
          paid_date: t.paid_date,
          payment_mode: paymentMode,
          receipt_no: t.payment_id || "—",
          discount,
          is_due: isDue,
          due_from: t.due_from_term_no,
        });

        if (isPaid) {
          paymentList.push({
            fee_type: ft.fee_type_name,
            term_no: t.term_no,
            is_due: isDue,
            due_from: t.due_from_term_no,
            amount: paidAmt,
            discount,
            paid_date: t.paid_date,
            payment_mode: paymentMode,
            receipt_no: t.payment_id || "—",
          });
        }
      });
    });

    const due = Math.max(0, totalFee - totalPaid);
    return { totalFee, totalPaid, due, rows, paymentList };
  };

  renderSummaryCards = (totalFee, totalPaid, due, academicYearText) => {
    const cards = [
      {
        label: "Term Fee",
        value: rupee(totalFee),
        icon: "fa-money-bill-wave",
        iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
        note: academicYearText,
        noteColor: "#16a34a",
      },
      {
        label: "Paid",
        value: rupee(totalPaid),
        icon: "fa-check-circle",
        iconBg: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
        note: "▲ Paid",
        noteColor: "#16a34a",
      },
      {
        label: "Due",
        value: rupee(due),
        icon: "fa-hourglass-half",
        iconBg: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
        note: "▼ pending",
        noteColor: "#dc2626",
      },
    ];

    return (
      <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
        {cards.map((m) => (
          <div
            key={m.label}
            className="d-flex align-items-start justify-content-between p-3 flex-grow-1"
            style={{
              minWidth: "220px",
              borderRadius: "16px",
              background: "#ffffff",
              border: "1px solid #f1f2f5",
              boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
            }}
          >
            <div>
              <span className="d-block text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>
                {m.label}
              </span>
              <strong
                className="d-block text-dark"
                style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}
              >
                {m.value}
              </strong>
              <span className="font-weight-bold" style={{ fontSize: "12px", color: m.noteColor }}>
                {m.note}
              </span>
            </div>
            <div
              className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: m.iconBg,
                boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
              }}
            >
              <i className={`fas ${m.icon}`} style={{ fontSize: "18px" }}></i>
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderFeeStatus = (totalFee, totalPaid, due) => {
    const pct = totalFee > 0 ? Math.round((totalPaid / totalFee) * 100) : 0;
    return (
      <div
        className="p-3 mt-4"
        style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
      >
        <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Fee Status
        </h6>

        <div
          style={{
            position: "relative",
            height: "12px",
            borderRadius: "20px",
            background: "#eef2f6",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: "20px",
              background: "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
              transition: "width 0.4s ease",
            }}
          />
        </div>

        <div className="d-flex justify-content-between mt-2" style={{ fontSize: "13px", color: "#64748b" }}>
          <span>{rupee(totalPaid)} paid</span>
          <span className="font-weight-bold">{pct}%</span>
          <span>{rupee(due)} due</span>
        </div>

        <div className="d-flex align-items-center mt-3" style={{ gap: "12px" }}>
          <button
            type="button"
            className="d-inline-flex align-items-center text-white font-weight-bold"
            style={{
              gap: "8px",
              border: "none",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "14px",
              background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
              cursor: due > 0 ? "pointer" : "not-allowed",
              opacity: due > 0 ? 1 : 0.6,
            }}
            disabled={due <= 0}
          >
            <i className="fas fa-credit-card" style={{ fontSize: "13px" }}></i>
            Pay {rupee(due)}
          </button>
          <button
            type="button"
            className="d-inline-flex align-items-center font-weight-bold"
            style={{
              gap: "8px",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "14px",
              background: "#ffffff",
              color: "#475569",
              cursor: "pointer",
            }}
          >
            <i className="far fa-bell" style={{ fontSize: "13px" }}></i>
            Send Reminder
          </button>
        </div>
      </div>
    );
  };

  renderHistoryTable = (rows, paymentList, totalPaid) => {
    const columns = [
      { dataField: "date", text: "DATE", sort: true },
      { dataField: "description", text: "DESCRIPTION", sort: true },
      {
        dataField: "amount",
        text: "AMOUNT",
        sort: true,
        formatter: (cell) => <strong className="text-dark">{rupee(cell)}</strong>,
      },
      { dataField: "receipt", text: "RECEIPT", formatter: makeReceiptFormatter(this.viewPDF) },
    ];

    return (
      <div
        className="p-3 mt-4"
        style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Payment History &amp; Receipts
          </h6>
          {paymentList.length > 0 && (
            <button
              type="button"
              onClick={() => this.downloadAllPayments(paymentList, totalPaid)}
              title="Download all payment history"
              className="d-inline-flex align-items-center"
              style={{
                gap: "7px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "12px",
                fontWeight: 700,
                color: "#1d4ed8",
                cursor: "pointer",
              }}
            >
              <i className="fas fa-download" /> Download All
            </button>
          )}
        </div>
        <CardLessDataTableWithSearch
          parentData={rows}
          particlecolumns={columns}
          name="fee_payment_history"
          placeholder="Search payment"
        />
      </div>
    );
  };

  render() {
    const { student } = this.props;
    const report = this.props.getStudentFeeReport || {};
    const loading = report.loading;
    const feeReport = report.getStudentFeeReport || [];

    const academicYearText = student?.academic_year_name || student?.year_name || "";
    const { totalFee, totalPaid, due, rows, paymentList } = this.buildModel(feeReport);

    return (
      <TabSection
        icon="fa-credit-card"
        title="Fees"
        description="Fee structure, paid terms and pending dues."
      >
        {loading ? (
          <FeePageShimmer />
        ) : feeReport.length === 0 ? (
          <div className="text-muted" style={{ fontSize: "14px" }}>
            No fee records found for {student?.student_name || "this student"}.
          </div>
        ) : (
          <div>
            {this.renderSummaryCards(totalFee, totalPaid, due, academicYearText)}
            {this.renderFeeStatus(totalFee, totalPaid, due)}
            {this.renderHistoryTable(rows, paymentList, totalPaid)}
          </div>
        )}
      </TabSection>
    );
  }
}

export default withRouter(
  compose(
    graphql(GET_STUDENT_FEE_REPORT, {
      options: (props) => ({
        variables: buildVariables(props.student),
        fetchPolicy: "network-only",
      }),
      name: "getStudentFeeReport",
    }),
    graphql(GET_ORGANISATION_PROFILE, {
      options: () => ({
        variables: { institution_id: parseInt(Cookies.get("institutionid")) || 0 },
        fetchPolicy: "cache-and-network",
      }),
      name: "getOrganisationProfile",
    }),
    graphql(FETCH_PAYMENT_MODES, {
      options: () => ({
        variables: { id: parseInt(0) },
        fetchPolicy: "cache-and-network",
      }),
      name: "getPaymentModes",
    })
  )(FeesTab)
);
